<script>
import { m } from "$lib/paraglide/messages.js";
import { formatPublishedAt, formatDate } from "$lib/utils";

let {
  //
  metadata,
  locale,
  fetchpriority = "high",
  loading = "eager",
  decoding = "async",
} = $props();

const { title } = $derived(metadata);
const { categories } = $derived(metadata);
const { image } = $derived(metadata);
const { video } = $derived(metadata);
const { videoPoster } = $derived(metadata);
const { publishedAt } = $derived(metadata);
// const { createdAt } = $derived(metadata);
const { updatedAt } = $derived(metadata);
const { timeToRead } = $derived(metadata);

const imageModules = import.meta.glob(`$assets/images/*.{jpg,jpeg,png}`, {
  eager: true,
  query: {
    enhanced: true,
    w: "540;768;1080;1366;1536;1920;2560;3000;4096;5120",
  },
});

const hasImage = $derived(Boolean(imageModules[`/src/assets/images/${image}`]));

// Derived values for better semantics
const publishedDateISO = $derived(new Date(publishedAt).toISOString());
const updatedDateISO = $derived(updatedAt ? new Date(updatedAt).toISOString() : null);
const readingTimeMinutes = $derived(timeToRead ? Math.ceil(parseInt(timeToRead.text) || 0) : null);
const readingTimeText = $derived(
  timeToRead?.text || (locale === "tr" ? "bir dakikadan az okuma süresi" : "less than a minute read"),
);
const shouldShowUpdatedDate = $derived(
  updatedAt && publishedAt && new Date(updatedAt).getTime() !== new Date(publishedAt).getTime(),
);
</script>

<header class="post-header" itemscope itemtype="https://schema.org/Article">
  <!-- Hero Image -->
  {#if imageModules && hasImage}
    <figure
      class="post-hero-image not-prose mb-5 w-full"
      itemprop="image"
      itemscope
      itemtype="https://schema.org/ImageObject"
    >
      <div class="not-prose overflow-hidden rounded-xl shadow-lg">
        <enhanced:img
          class="w-full object-cover"
          alt={title}
          src={imageModules[`/src/assets/images/${image}`].default}
          sizes="(min-width: 1024px) 65vw, 100vw"
          itemprop="url"
          loading={loading}
          decoding={decoding}
          fetchpriority={fetchpriority}
        ></enhanced:img>
      </div>
      <meta itemprop="description" content={title} />
    </figure>
  {/if}

  <!-- Hero Video -->
  {#if video}
    <figure class="post-hero-video not-prose mb-10">
      <div class="overflow-hidden rounded-xl shadow-lg">
        <video
          width="720"
          height="auto"
          poster={videoPoster}
          autoplay
          loop
          muted
          playsinline
          class="w-full"
          aria-label={title}
        >
          <source src={video} type="video/mp4" />
          <track kind="captions" />
          <p>{m.your_browser_does_not_support_the_video_tag()}</p>
        </video>
      </div>
    </figure>
  {/if}

  <!-- Post Metadata -->
  <div class="post-meta not-prose w-full">
    <div
      class="mb-5 flex w-full flex-wrap items-center gap-3 border-b border-base-6 py-3 text-sm font-medium text-low-contrast sm:gap-4"
      role="contentinfo"
      aria-label="Post metadata"
    >
      <!-- Categories -->
      {#if categories}
        <div class="post-categories flex flex-wrap gap-2" itemprop="articleSection">
          {#each categories.map((c) => c.trim()) as category (category)}
            <span class="inline-flex items-center rounded-full bg-base-3 px-3 py-1 text-xs font-semibold">
              {category}
            </span>
          {/each}
        </div>
      {/if}

      <!-- Reading Time -->
      {#if readingTimeText}
        <div class="post-reading-time flex items-center gap-1.5" aria-label="Estimated reading time">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <span itemprop="timeRequired" content={readingTimeMinutes ? `PT${readingTimeMinutes}M` : "PT1M"}>
            {readingTimeText}
          </span>
        </div>
      {/if}

      <!-- Published Date -->
      <div class="post-published flex items-center gap-1.5 md:ml-auto" aria-label="Published date">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          ></path>
        </svg>
        <time
          itemprop="datePublished"
          datetime={publishedDateISO}
          aria-label={`Published on ${formatDate(publishedAt, { locale })}`}
        >
          {formatDate(publishedAt, { locale })}
        </time>
      </div>

      <!-- Updated Date -->
      {#if shouldShowUpdatedDate && updatedDateISO}
        <div class="post-updated flex items-center gap-1.5 text-xs" aria-label="Last updated">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            ></path>
          </svg>
          <time
            itemprop="dateModified"
            datetime={updatedDateISO}
            aria-label={`Updated on ${formatPublishedAt(updatedAt, locale)}`}
          >
            {formatPublishedAt(updatedAt, locale)}
          </time>
        </div>
      {/if}
    </div>
  </div>

  <!-- Hidden Microdata -->
  <meta itemprop="headline" content={title} />
  <meta itemprop="inLanguage" content={locale} />
</header>
